import { useMutation, useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import relative from 'dayjs/plugin/relativeTime';
import { useSession } from 'next-auth/react';
import { useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import config from '../config';
import { getCooldownDetails } from '../firebase-client';
import { getAssets } from '../utils/getAssets';
import { calculateCost, calculateOddsOfWinning } from '../utils/vending-machine';
import { useAlgo } from './useAlgo';
import { useAlgoPriceQuery } from './useAlgoPriceQuery';
import { useBalanceStore } from './useBalanceStore';
import { useKittiesStore } from './useKittiesStore';
import useNfd from './useNfd';
import { useUserAssetsQuery } from './useUserAssetsStore';

dayjs.extend(relative);

export const useVendingMachineAssetsQuery = () => {
  return useQuery({
    queryKey: ['vmAssets'],
    queryFn: async () => {
      const vendingMachineAssetsAll = await getAssets(process.env.NEXT_PUBLIC_VENDING_MACHINE_WALLET);
      const vendingMachineAssets = vendingMachineAssetsAll.filter((asset) => asset.amount > 0);
      return vendingMachineAssets;
    },
    refetchInterval: 10000,
    refetchOnWindowFocus: false,
  });
};

export const useVendingMachineCooldownQuery = (address) => {
  return useQuery({
    queryKey: ['cooldown', address],
    queryFn: () => getCooldownDetails(address),
    enabled: !!address,
  });
};

export const useMemeCoinDetails = (assetId) => {
  return useQuery({
    queryKey: ['memeCoinDetails', assetId],
    queryFn: async () => {
      const response = await fetch(`https://free-api.vestige.fi/asset/${assetId}/list`);
      const data = await response.json();
      return data || null;
    },
    onError: (error) => {
      console.error(error);
    },
  });
};

export const useVendingMachine = () => {
  const { data: session } = useSession();
  const { address, purchaseFromVendingMachine } = useAlgo();
  const vmAssetsQuery = useVendingMachineAssetsQuery();
  const vmCooldownQuery = useVendingMachineCooldownQuery(address);
  const userAssetsQuery = useUserAssetsQuery({ address });
  const { myKitties, myLegendaryKitties } = useKittiesStore();
  const { nfd } = useNfd({ address });
  const [winnings, setWinnings] = useState(null);
  const { vmTokenBalance } = useBalanceStore();
  const { data: algoPriceData } = useAlgoPriceQuery();
  const { algo_price_in_usd } = algoPriceData || {};

  const vmAssets = vmAssetsQuery.data || [];
  const vmAssetsCount = vmAssets
    .filter((asset) => !config.vmMemeCoins.find((coin) => coin.assetId === asset['asset-id']))
    .reduce((acc, asset) => acc + asset.amount, 0);

  const vmAssetPreviews = useMemo(() => {
    return vmAssets
      .filter((asset) => !config.vmMemeCoins.find((coin) => coin.assetId === asset['asset-id']) && asset['asset-id'] !== 2217748679)
      .sort((a, b) => Math.random() - 0.5)
      .slice(0, 6);
  }, [vmAssets]);

  const firstMemeCoinTotalSupply =
    (vmAssets.find((asset) => asset['asset-id'] === config.vmMemeCoins?.[0]?.assetId)?.amount || 0) / 10 ** config.vmMemeCoins?.[0]?.decimals;
  const { data: firstMemeCoinDetails } = useMemeCoinDetails(config.vmMemeCoins?.[0]?.assetId);
  const firstMemeCoinPrice = firstMemeCoinDetails?.price;

  const memeCoinValue = (firstMemeCoinTotalSupply || 0) * (firstMemeCoinPrice || 0) * (algo_price_in_usd || 0);
  const { purchaseCount, nextPurchaseAt } = vmCooldownQuery.data || { purchaseCount: 0, nextPurchaseAt: null };

  const { discountedCost, originalCost } = calculateCost({ nfd, myKitties, myLegendaryKitties, purchaseCount });
  const oddsOfWinning = calculateOddsOfWinning(vmTokenBalance);

  const enabledOnDate = useMemo(() => {
    return new Date(process.env.NEXT_PUBLIC_VENDING_MACHINE_ENABLED_ON_DATE);
  }, []);

  const formattedEnabledOnDate = useMemo(() => {
    return dayjs(enabledOnDate).format('MMMM D, YYYY h:mm A z');
  }, [enabledOnDate]);
  const isEnabledBasedOnDate = useMemo(() => {
    return dayjs(enabledOnDate).isBefore(dayjs());
  }, [enabledOnDate]);

  const correctDiscordAccount = useMemo(() => {
    return session?.user?.address === address && session?.user?.discord?.id;
  }, [session, address]);

  const canPurchase = useMemo(() => {
    return myKitties.length > 0 && isEnabledBasedOnDate && vmAssetsCount > 0 && !!correctDiscordAccount && purchaseCount < 2;
  }, [myKitties, isEnabledBasedOnDate, vmAssetsCount, correctDiscordAccount, purchaseCount]);

  const purchaseMutate = useMutation({
    mutationFn: async () => {
      if (vmAssets.length === 0) {
        toast.error('The vending machine is empty!');
        return;
      }

      const { data } = await purchaseFromVendingMachine({ cost: discountedCost, vmTokenBalance });

      return data?.nft;
    },
    onMutate: () => {
      setWinnings(null);
    },
    onSuccess: (data) => {
      setWinnings(data);
      vmAssetsQuery.refetch();
      vmCooldownQuery.refetch();
      userAssetsQuery.refetch();

      toast.success('Purchase successful!');
    },
    onError: (error) => {
      const theError = typeof error.response?.data === 'string' ? error.response?.data : error.response?.data.error || error.message;
      toast.error(theError || 'Something went wrong. Please try again later.');
    },
  });

  return {
    vmAssetsQuery,
    vmAssets,
    vmAssetPreviews,
    vmAssetsCount,
    vmPurchaseCount: purchaseCount,
    vmLastPurchase: nextPurchaseAt,
    vmTokenBalance,
    memeCoinValue,
    correctDiscordAccount,
    canPurchase,
    discountedCost,
    originalCost,
    enabledOnDate,
    formattedEnabledOnDate,
    isEnabledBasedOnDate,
    winnings,
    purchaseMutate,
    oddsOfWinning,
  };
};
